import { classnames } from 'Utils/classnames/classnames.utils';
import type { ReactNode } from 'react';
import React, { Children, useState } from 'react';
import { Collapse, UnmountClosed } from 'react-collapse';
import { ICONS } from 'Components/utils/icon/icons';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { generateUniqueId } from 'Utils/unique-id/uniqueId';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Toggler } from 'Components/frameworks/toggler/Toggler';
import { themes } from './CollapsePanel.themes';
const POSITIONS = {
  BOTTOM: 'bottom',
  TOP: 'top',
};
type CollapsePanelProps = {
  as?: string;
  children: ReactNode;
  className?: string;
  collapseIconName?: IconName;
  collapseLabel?: string;
  expandIconName?: IconName;
  expandLabel?: string;
  hasKeepCollapsedContent?: boolean;
  headerClassName?: string;
  iconName?: IconName;
  iconSize?: string;
  id?: string;
  isExpanded?: boolean;
  isSemiTransparent?: boolean;
  loadMoreButtonPosition?: (typeof POSITIONS)[keyof typeof POSITIONS];
  noIcons?: boolean;
  onClick?: () => void;
  theme?: keyof typeof themes;
  title?: ReactNode;
};

const hasLabels = (
  { collapseIconName, collapseLabel, expandIconName, expandLabel }: CollapsePanelProps,
  isActive: boolean,
) => (isActive ? collapseIconName || collapseLabel : expandIconName || expandLabel);

type LoadMoreButtonProps = CollapsePanelProps & {
  ariaId: string;
  isActive: boolean;
  isDesktop: boolean;
  toggle: () => void;
};

const LoadMoreButton = ({
  ariaId,
  isActive,
  isDesktop,
  toggle,
  ...collapsePanelProps
}: LoadMoreButtonProps) => {
  const {
    as: HeaderTag = 'div',
    collapseLabel,
    collapseIconName = ICONS.CROSS,
    expandLabel,
    expandIconName = ICONS.ARROW_DOWN,
    headerClassName = '',
    iconName,
    iconSize,
    noIcons,
    theme = 'default',
    title,
  } = collapsePanelProps;
  const {
    thButton,
    thHeader,
    thHeaderDecorator,
    thHeaderDecoratorActive,
    thLabels,
    thLabel,
    thLabelIcon,
    thLabelIconActive,
    thLabelIconWidth,
    thTitle,
    thTitleHasEllipsis,
    thTitleIcon,
    thTitleIconSize,
  } = themes[theme]({
    isDesktop,
  });
  return (
    <HeaderTag className={classnames(headerClassName, thHeader)}>
      <button
        aria-controls={ariaId}
        aria-expanded={isActive}
        className={thButton}
        onClick={toggle}
        type="button"
      >
        {!!title && (
          <span className={thTitle}>
            <Icon
              className={classnames('shrink-0', thTitleIcon)}
              name={iconName}
              width={iconSize || thTitleIconSize || '1em'}
            />
            {thTitleHasEllipsis ? <span className="w-full truncate">{title}</span> : title}
          </span>
        )}
        {hasLabels(collapsePanelProps, isActive) && (
          <span className={thLabels}>
            <span className={thLabel} data-testid="CollapsePanel-label">
              {isActive ? collapseLabel : expandLabel}
            </span>
            {!noIcons && (
              <Icon
                className={classnames(thLabelIcon, {
                  [thLabelIconActive]: isActive,
                })}
                name={isActive ? collapseIconName : expandIconName}
                width={thLabelIconWidth}
              />
            )}
          </span>
        )}
      </button>
      {(thHeaderDecorator || thHeaderDecoratorActive) && (
        <div
          className={classnames(thHeaderDecorator, {
            [thHeaderDecoratorActive]: isActive,
          })}
        />
      )}
    </HeaderTag>
  );
};

export const CollapsePanel = (props: CollapsePanelProps) => {
  const { isDesktop } = useResponsive();
  const [isVisible, setVisible] = useState(props.isExpanded);

  if (!Children.count(props.children)) {
    return null;
  }

  const {
    children,
    className,
    hasKeepCollapsedContent = true,
    id,
    isExpanded,
    isSemiTransparent,
    loadMoreButtonPosition = POSITIONS.TOP,
    onClick,
    theme = 'default',
    title,
  } = props;
  const { thBody, thCollapsedTagInitialStyle, thContent, thContentInner, thRoot } = themes[theme]({
    isDesktop,
  });
  const ariaId = generateUniqueId([id || title || ''], 'CollapsePanel_');
  const CollapsedTag = hasKeepCollapsedContent ? Collapse : UnmountClosed;
  return (
    <Toggler isActive={isExpanded} onToggle={onClick}>
      {(toggle, isActive) => (
        <section className={classnames(thRoot, className)} id={id}>
          {loadMoreButtonPosition === POSITIONS.TOP && (
            <LoadMoreButton
              {...props}
              ariaId={ariaId}
              isActive={isActive}
              isDesktop={isDesktop}
              toggle={toggle}
            />
          )}
          <div
            aria-hidden={!isActive}
            className={classnames(thBody, {
              invisible: !isVisible,
            })}
            id={ariaId}
          >
            <CollapsedTag
              initialStyle={
                isSemiTransparent
                  ? {
                      height: 'auto',
                      overflow: 'initial',
                      ...thCollapsedTagInitialStyle,
                    }
                  : thCollapsedTagInitialStyle
              }
              isOpened={isActive}
              onWork={() => {
                setVisible(true);
              }}
              onRest={({ isFullyClosed }) => {
                setVisible(!isFullyClosed);
              }}
            >
              <div className={thContent}>
                <div className={thContentInner}>{children}</div>
              </div>
            </CollapsedTag>
          </div>
          {loadMoreButtonPosition === POSITIONS.BOTTOM && (
            <LoadMoreButton
              {...props}
              ariaId={ariaId}
              isActive={isActive}
              isDesktop={isDesktop}
              toggle={toggle}
            />
          )}
        </section>
      )}
    </Toggler>
  );
};
